import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import getTheme from './themes/theme';
import Login from './components/Login/Login';
import PreLoginGate from './components/PreLoginGate/PreLoginGate';
import NiceLogin from './components/NiceLogin/NiceLogin';
import ChangePassword from './components/ChangePassword/ChangePassword';
import NoLicenceActivated from './components/NoLicenceActivated/NoLicenceActivated';
import RegisterViaCodePage from './components/RegisterViaCodePage/RegisterViaCodePage';
import PseudoAnonymizerPage from './components/PseudoAnonymizerPage/PseudoAnonymizerPage';

import LanguageSwitcher from './components/LanguageSwitcher/LanguageSwitcher';

// Tenant admin
import MassCreateCredentialsPage from './components/MassCreateCredentialsPage/MassCreateCredentialsPage';
import TenantSettingsPage from './components/TenantSettingsPage/TenantSettingsPage';
import TenantContactsPage from './components/TenantContactsPage/TenantContactsPage';
import TenantUsersPage from './components/TenantUsersPage/TenantUsersPage';
import IssueRegLinkPage from './components/IssueRegLinkPage/IssueRegLinkPage';

import UserFilesPage from './components/UserFilesPage/UserFilesPage';
import TwoFactorSettingsPage from './components/TwoFactorSettingsPage/TwoFactorSettingsPage';
import AnonymizationProfilesPage from './components/AnonymizationProfilesPage/AnonymizationProfilesPage';
import ManualPage from './components/ManualPage/ManualPage';
import AboutAppPage from './components/AboutAppPage/AboutAppPage';
import SuperAdminManualPage from './components/SuperAdminManualPage/SuperAdminManualPage';
import PIIAnonymizerPage from './components/PIIAnonymizerPage/PIIAnonymizerPage';
import PIIHistoryPage from './components/PIIHistoryPage/PIIHistoryPage';
import ModulesPage from './components/ModulesPage/ModulesPage';
import SecureEmbedPage from './components/SecureEmbedPage/SecureEmbedPage';

import PIIValidatorPage from './components/PIIValidatorPage/PIIValidatorPage';
import PIIValidationHistoryPage from './components/PIIValidationHistoryPage/PIIValidationHistoryPage';

// Pseudo DICOM Anonymization
import PseudoDicomAnonymizerPage from './components/PseudoDicomAnonymizerPage/PseudoDicomAnonymizerPage';
import PseudoDicomDePseudonymizerPage from './components/PseudoDicomDePseudonymizerPage/PseudoDicomDePseudonymizerPage';
import PseudoDicomProfilesPage from './components/PseudoDicomProfilesPage/PseudoDicomProfilesPage';

import GlassSnack from './shared/GlassSnack';
import { SnackbarProvider } from 'notistack';
import ThemeSwitcher from './components/ThemeSwitcher/ThemeSwitcher';

// Super admin
import AdminBillingPage from './components/AdminBillingPage/AdminBillingPage';
import SuperAdminTenantsPage from './components/SuperAdminTenantsPage/SuperAdminTenantsPage';
import SuperAdminTenantsRegLinksPage from './components/SuperAdminTenantsRegLinksPage/SuperAdminTenantsRegLinksPage';
import OnboardingPage from './components/OnboardingPage/OnboardingPage';

// constants
import { PROCESS_TYPES } from './constants/processTypes';

function App() {
  // useEffect(() => { document.body.style.backgroundColor = '#333333' }, [])
  const [mode, setMode] = useState('light'); // default dark

  // optional: load saved mode from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'light' || saved === 'dark') {
      setMode(saved);
    }
  }, []);

  // optional: save mode when it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const theme = useMemo(() => getTheme(mode), [mode]);

  const toggleMode = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={3000}
        Components={{
          default: GlassSnack,
          success: GlassSnack,
          error: GlassSnack,
          warning: GlassSnack,
          info: GlassSnack,
        }}
      >
        <Router basename={process.env.REACT_APP_BASENAME}>
          <Routes>
            <Route path='/' element={<PreLoginGate />} />
            <Route path='/login' element={<Login />} />
            <Route path='/nice-login' element={<NiceLogin />} />

            {/* Super admin */}
            <Route path='/admin/billing' element={<AdminBillingPage />} />
            <Route path='/admin/superuser-tenants' element={<SuperAdminTenantsPage />} />
            <Route path='/admin/onboarding' element={<OnboardingPage />} />
            <Route path='/admin/manual' element={<SuperAdminManualPage />} />
            <Route path='/admin/superuser-tenants-reg-links' element={<SuperAdminTenantsRegLinksPage />} />

            {/* Tenant admin */}
            <Route path='/tenant-settings' element={<TenantSettingsPage />} />
            <Route path='/tenant-contacts' element={<TenantContactsPage />} />
            <Route path='/tenant-users' element={<TenantUsersPage />} />
            <Route path='/mass-create-credentials' element={<MassCreateCredentialsPage />} />
            <Route path='/issue-registration-link' element={<IssueRegLinkPage />} />

            {/* Profile */}
            <Route path='/change-password' element={<ChangePassword />} />
            <Route path='/2fa-settings' element={<TwoFactorSettingsPage />} />

            {/* Anonymization */}
            <Route
              path="/anonymization-profiles"
              element={<AnonymizationProfilesPage processType={PROCESS_TYPES.ANONYMIZATION} />}
            />
            <Route path='/pii-anonymizer' element={<PIIAnonymizerPage />} />
            <Route path='/anonymized-files' element={<UserFilesPage processType={PROCESS_TYPES.ANONYMIZATION} />} />
            <Route path='/pii-history' element={<PIIHistoryPage />} />

            {/* PseudoAnonymization */}
            <Route
              path="/pseudoanonymization-profiles"
              element={<AnonymizationProfilesPage processType={PROCESS_TYPES.PSEUDONYMIZATION} />}
            />
            <Route path='/pseudo-anonymizer' element={<PseudoAnonymizerPage />} />
            <Route path='/pseudoanonymized-files' element={<UserFilesPage processType={PROCESS_TYPES.PSEUDONYMIZATION} />} />
            <Route path='/pseudo-anonymization-history' element={<PIIHistoryPage />} />

            {/* Validation */}
            <Route path='/pii-validator' element={<PIIValidatorPage />} />

            {/* Manual / prirucka */}
            <Route path='/manual' element={<ManualPage />} />
            <Route path='/pii-validation-history' element={<PIIValidationHistoryPage />} />

            {/* Detection */}
            <Route path='/detection-legacy' element={<ModulesPage url={'https://test.cenzor.sk/'} />} />
            <Route path='/detection' element={<SecureEmbedPage resource='cenzor/full' />} />

            {/* Pseudo DICOM Anonymization */}
            <Route path='/pseudo-dicom-pseudonymizer' element={<PseudoDicomAnonymizerPage />} />
            <Route path='/pseudo-dicom-depseudonymizer' element={<PseudoDicomDePseudonymizerPage />} />
            <Route path='/pseudo-dicom-profiles' element={<PseudoDicomProfilesPage />} />

            {/* Other */}
            <Route path='/about-app' element={<AboutAppPage />} />
            <Route path='/register-via-code' element={<RegisterViaCodePage />} />
            <Route path='/no-licence' element={<NoLicenceActivated />} />
          </Routes>
        </Router>
        {/* top-right controls */}
        <LanguageSwitcher />
        <ThemeSwitcher mode={mode} toggleMode={toggleMode} />
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
